import { useCallback } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { webContainerClient } from "@editor/trpc/webContainer";

import { useNextDevServer } from "@/features/code-execution/context";

/**
 * Hook to fetch and update files relevant to a list of pages in the
 * WebContainer Filesystem.
 */
export const useUpdatePageFilesInWebContainer = () => {
  const projectId = useCurrentProjectId();
  const { mountAdditionalFiles } = useNextDevServer();
  const { invalidatePageImportErrors } = useNextDevServer();
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const updatePageFiles = useCallback(
    async ({ pages }: { pages: { id: string; fileVersionId?: string }[] }) => {
      if (!projectId) {
        return;
      }

      const { fileTree, pageRoutes } =
        await webContainerClient.getFileTreeForPages.query({
          projectId,
          pages,
        });

      if (!isServerSandboxEnabled) {
        await mountAdditionalFiles?.(fileTree);
      }

      invalidatePageImportErrors(pageRoutes);
    },
    [
      projectId,
      invalidatePageImportErrors,
      mountAdditionalFiles,
      isServerSandboxEnabled,
    ],
  );

  return updatePageFiles;
};
